import React from "react";
import { Box, ThemeProvider } from "@mui/system";
import { Tooltip, IconButton, Chip } from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../../RnDMember/GenericRnDListing/GenericRnDListing";
import { setData } from "@Redux/requestDataSlice";
import {
  stringCustomOperators,
  numberCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import { getStatusColor } from "../../../Utils/Validations/HelperFunctions/StyleFunction";

const RD_COORDINATOR_RESPONSES = "/exit-request-response/my-submissions";
const PAGE_TITLE = "My Submitted Product Lists";
const ERROR_MESSAGE = "Failed to fetch coordinator impact assessments.";
const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_VALUE = "-";
const ICON_SIZE = "small";

const formatDate = (dateString) =>
  dateString ? dayjs(dateString).format(DATE_FORMAT) : DEFAULT_VALUE;

const getNestedValue = (obj, path) => {
  const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return value || DEFAULT_VALUE;
};

function CoordinatorResponsesListing() {
  const dispatch = useDispatch();

  const defaultColumns = [
    {
      field: "ingredient",
      headerName: "Ingredient",
      flex: 1.2,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.exitRequest?.ingredient?.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.exitRequest?.ingredient?.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      valueGetter: (params) =>
        getNestedValue(params.row, "exitRequest.categoryName"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "primaryDate",
      headerName: "Primary Date",
      flex: 1.2,
      valueGetter: (params) => {
        const d = params.row.exitRequest?.primaryDate;
        return d ? new Date(d) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.6,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "createdAt",
      headerName: "Submitted On",
      flex: 0.8,
      valueGetter: (params) => {
        const d = params.row.createdAt;
        return d ? new Date(d) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      disableReorder: true,
    },
  ];

  const initialColumnVisibilityModel = {};

  const handleViewResponse = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.rdCoordinatorResponseFromResponses
        .replace(":reqId", row.exitRequest?.id)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      <Tooltip title="View / Edit Product List">
        <IconButton
          color="primary"
          onClick={() => handleViewResponse(params.row, navigate)}
          size={ICON_SIZE}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  return (
    <GenericRnDListing
      title={PAGE_TITLE}
      PageIcon={<MarkEmailReadIcon color="white" fontSize="large" />}
      iconColor="primary.main"
      apiRoute={RD_COORDINATOR_RESPONSES}
      defaultColumns={defaultColumns}
      renderActions={renderActions}
      errorMessage={ERROR_MESSAGE}
      buttons={[]}
      serverPagination={true}
      getRowId={(row) => `${row.exitRequest?.id}_${row.categoryAssignmentId}`}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
    />
  );
}

export default CoordinatorResponsesListing;
